import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  TextField,
  Button,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  TablePagination,
  LinearProgress,
  Alert,
  Chip,
  MenuItem,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tabs,
  Tab,
  Tooltip,
} from '@mui/material';
import {
  MdSearch,
  MdRefresh,
  MdInfoOutline,
  MdFileDownload,
  MdGridOn,
  MdPrint,
  MdAccountBalance,
  MdReceiptLong,
  MdPayments,
  MdHourglassBottom,
  MdSavings,
  MdTimer,
  MdCreditCardOff,
} from 'react-icons/md';
import { getData } from '../../Webservice/Webservice';
import {
  FONT,
  inr,
  fmtDate,
  toISO,
  authToken,
  PRESETS,
  presetRange,
  rangeLabel,
  StatCard,
  statGridSx,
  exportButtonSx,
  downloadCsv,
  downloadExcel,
  openPrintView,
} from './reportShared';

const REPORT_ROUTE = 'reports/supplier-ledger';
const pill = (bg, fg) => ({ fontWeight: 700, fontFamily: FONT, background: bg, color: fg, height: 24, fontSize: 11 });
const dash = <Typography component="span" sx={{ color: '#94a3b8' }}>—</Typography>;
const AGE = [
  { key: '0-30', label: '0–30 days', colors: ['#dcfce7', '#166534'] },
  { key: '31-60', label: '31–60 days', colors: ['#fef3c7', '#92400e'] },
  { key: '61-90', label: '61–90 days', colors: ['#ffedd5', '#9a3412'] },
  { key: '90+', label: 'Over 90 days', colors: ['#fee2e2', '#991b1b'] },
];
const ageColors = (key) => (AGE.find((a) => a.key === key) || AGE[3]).colors;

const TwoLine = ({ top, bottom }) => (
  <Box sx={{ lineHeight: 1.25, minWidth: 160 }}>
    <Typography sx={{ fontSize: 11.5, fontWeight: 700, fontFamily: FONT }}>{top}</Typography>
    {bottom && <Typography sx={{ fontSize: 11.5, color: '#64748b', fontFamily: FONT }}>{bottom}</Typography>}
  </Box>
);

const col = (key, lbl, opts = {}) => ({ key, label: lbl, ...opts });
const money = (key, lbl, extra = {}) => col(key, lbl, { numeric: true, text: (r) => r[key], cell: (r) => <span style={{ color: r[key] < 0 ? '#15803d' : undefined }}>{inr(r[key])}</span>, print: (r) => inr(r[key]), ...extra });
const ageMoney = (key, lbl) =>
  col(`age_${key}`, lbl, {
    numeric: true,
    text: (r) => r[`age_${key}`],
    print: (r) => inr(r[`age_${key}`]),
    cell: (r) => (r[`age_${key}`] ? <span style={{ color: ageColors(key)[1], fontWeight: 700 }}>{inr(r[`age_${key}`])}</span> : dash),
  });

const TABS = [
  {
    key: 'balances',
    label: 'Supplier Balances',
    defaultSort: 'outstanding_desc',
    sorts: [['outstanding_desc', 'Outstanding: high to low'], ['oldest_desc', 'Oldest unpaid first'], ['invoiced_desc', 'Invoiced: high to low'], ['name_asc', 'Name: A to Z']],
    columns: [
      col('supplier', 'Supplier', { text: (r) => r.supplier, cell: (r) => <TwoLine top={r.supplier} bottom={[r.supplierCode, r.gstin && `GSTIN ${r.gstin}`].filter(Boolean).join(' · ')} />, extra: (r) => ({ 'Supplier Code': r.supplierCode, GSTIN: r.gstin, Contact: r.contact }) }),
      money('openingBalance', 'Opening'),
      money('invoiced', 'Invoiced'),
      money('paid', 'Paid'),
      money('closingBalance', 'Closing', { bold: true }),
      col('outstanding', 'Outstanding Now', {
        numeric: true,
        bold: true,
        text: (r) => r.outstanding,
        print: (r) => inr(r.outstanding),
        extra: (r) => ({ Advance: r.advance, 'Open Invoices': r.openInvoices }),
        cell: (r) =>
          r.outstanding > 0 ? (
            <Box sx={{ lineHeight: 1.25 }}>
              <Typography sx={{ fontSize: 11.5, fontWeight: 800, color: '#b45309', fontFamily: FONT }}>{inr(r.outstanding)}</Typography>
              <Typography sx={{ fontSize: 11.5, color: '#64748b', fontFamily: FONT }}>{r.openInvoices} open invoice{r.openInvoices === 1 ? '' : 's'}</Typography>
            </Box>
          ) : r.advance > 0 ? (
            <Tooltip title="Paid more than invoiced — advance with supplier" arrow>
              <Chip size="small" label={`Advance ${inr(r.advance)}`} sx={pill('#dcfce7', '#166534')} />
            </Tooltip>
          ) : (
            <Chip size="small" label="Settled" sx={pill('#f1f5f9', '#475569')} />
          ),
      }),
      ...AGE.map((a) => ageMoney(a.key, a.label)),
      col('avgDaysToPay', 'Avg Days to Pay', { center: true, text: (r) => (r.avgDaysToPay === null ? '' : r.avgDaysToPay), cell: (r) => (r.avgDaysToPay === null ? dash : `${r.avgDaysToPay} d`) }),
      col('lastPayment', 'Last Payment', { text: (r) => (r.lastPayment ? fmtDate(r.lastPayment) : ''), cell: (r) => (r.lastPayment ? fmtDate(r.lastPayment) : dash) }),
      col('bank', 'Pay To', {
        text: (r) => (r.bankReady ? r.preferredMode || 'Bank on file' : 'Missing'),
        cell: (r) => (r.bankReady ? <Typography sx={{ fontSize: 11.5, fontFamily: FONT }}>{r.preferredMode || 'Bank on file'}</Typography> : <Chip size="small" label="No bank details" sx={pill('#fee2e2', '#b91c1c')} />),
      }),
    ],
  },
  {
    key: 'outstanding',
    label: 'Outstanding Invoices',
    defaultSort: 'days_desc',
    sorts: [['days_desc', 'Oldest first'], ['amount_desc', 'Outstanding: high to low']],
    columns: [
      col('invoiceNumber', 'Invoice No.', { bold: true, text: (r) => r.invoiceNumber }),
      col('invoiceDate', 'Invoice Date', { text: (r) => fmtDate(r.invoiceDate) }),
      col('supplier', 'Supplier', { text: (r) => r.supplier, cell: (r) => <TwoLine top={r.supplier} bottom={r.supplierCode} />, extra: (r) => ({ 'Supplier Code': r.supplierCode }) }),
      money('amount', 'Invoice Amount'),
      money('paid', 'Paid'),
      money('outstanding', 'Outstanding', { bold: true, cell: (r) => <span style={{ color: '#b45309', fontWeight: 800 }}>{inr(r.outstanding)}</span> }),
      col('days', 'Age', {
        center: true,
        text: (r) => `${r.days} days`,
        cell: (r) => {
          const [bg, fg] = ageColors(r.bucket);
          return <Chip size="small" label={`${r.days} days`} sx={pill(bg, fg)} />;
        },
      }),
      col('lastPaidAt', 'Last Paid', { text: (r) => (r.lastPaidAt ? fmtDate(r.lastPaidAt) : ''), cell: (r) => (r.lastPaidAt ? fmtDate(r.lastPaidAt) : dash) }),
    ],
  },
  {
    key: 'statement',
    label: 'Ledger Statement',
    defaultSort: 'date_desc',
    sorts: [['date_desc', 'Newest first'], ['date_asc', 'Oldest first']],
    columns: [
      col('date', 'Date', { text: (r) => fmtDate(r.date) }),
      col('supplier', 'Supplier', { text: (r) => r.supplier, cell: (r) => <TwoLine top={r.supplier} bottom={r.supplierCode} />, extra: (r) => ({ 'Supplier Code': r.supplierCode }) }),
      col('type', 'Type', { text: (r) => (r.type === 'invoice' ? 'Invoice' : 'Payment'), cell: (r) => <Chip size="small" label={r.type === 'invoice' ? 'Invoice' : 'Payment'} sx={r.type === 'invoice' ? pill('#e0e7ff', '#3730a3') : pill('#dcfce7', '#166534')} /> }),
      col('reference', 'Reference', { bold: true, text: (r) => r.reference }),
      col('note', 'Note', {
        text: (r) => r.note,
        cell: (r) =>
          r.note ? (
            <Tooltip title={r.note} arrow>
              <Typography sx={{ fontSize: 11.5, fontFamily: FONT, maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis' }}>{r.note}</Typography>
            </Tooltip>
          ) : (
            dash
          ),
      }),
      col('debit', 'Debit (Invoice)', { numeric: true, text: (r) => r.debit, print: (r) => (r.debit ? inr(r.debit) : ''), cell: (r) => (r.debit ? <span style={{ color: '#b45309', fontWeight: 700 }}>{inr(r.debit)}</span> : dash) }),
      col('credit', 'Credit (Payment)', { numeric: true, text: (r) => r.credit, print: (r) => (r.credit ? inr(r.credit) : ''), cell: (r) => (r.credit ? <span style={{ color: '#15803d', fontWeight: 700 }}>{inr(r.credit)}</span> : dash) }),
      money('balance', 'Running Balance', { bold: true }),
    ],
  },
];

const SupplierLedgerReport = () => {
  const [tabIndex, setTabIndex] = useState(0);
  const tab = TABS[tabIndex];
  const [preset, setPreset] = useState('all');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [supplier, setSupplier] = useState('');
  const [onlyDue, setOnlyDue] = useState('');
  const [bucket, setBucket] = useState('');
  const [sortBy, setSortBy] = useState(TABS[0].defaultSort);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState('');
  const [data, setData] = useState(null);
  const [notesOpen, setNotesOpen] = useState(false);

  useEffect(() => {
    const id = setTimeout(() => {
      setSearch(searchInput.trim());
      setPage(0);
    }, 400);
    return () => clearTimeout(id);
  }, [searchInput]);

  const filterParams = useMemo(() => {
    const p = { tab: tab.key, sortBy };
    if (from) p.from = from;
    if (to) p.to = to;
    if (search) p.search = search;
    if (supplier) p.supplier = supplier;
    if (onlyDue) p.onlyDue = onlyDue;
    if (tab.key === 'outstanding' && bucket) p.bucket = bucket;
    return p;
  }, [tab.key, sortBy, from, to, search, supplier, onlyDue, bucket]);

  const fetchReport = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await getData(REPORT_ROUTE, { ...filterParams, page: page + 1, limit: rowsPerPage }, authToken(), false);
      if (res?.success) setData(res);
      else setError(res?.message || 'Failed to load report');
    } catch (err) {
      setError(err.message || 'Failed to load report');
    } finally {
      setLoading(false);
    }
  }, [filterParams, page, rowsPerPage]);

  useEffect(() => {
    fetchReport();
  }, [fetchReport]);

  const goToTab = (i, after) => {
    setTabIndex(i);
    setSortBy(TABS[i].defaultSort);
    setPage(0);
    if (after) after();
  };
  const applyPreset = (key) => {
    const range = presetRange(key);
    setPreset(key);
    setFrom(range.from);
    setTo(range.to);
    setPage(0);
  };
  const onDateChange = (setter) => (e) => {
    setter(e.target.value);
    setPreset('');
    setPage(0);
  };
  const setFilter = (setter) => (e) => {
    setter(e.target.value);
    setPage(0);
  };
  const resetFilters = () => {
    setPreset('all');
    setFrom('');
    setTo('');
    setSearchInput('');
    setSearch('');
    setSupplier('');
    setOnlyDue('');
    setBucket('');
    setSortBy(tab.defaultSort);
    setPage(0);
  };

  const exportObjects = (rows) =>
    rows.map((r) => {
      const obj = {};
      for (const c of tab.columns) {
        obj[c.label] = c.text(r);
        if (c.extra) Object.assign(obj, c.extra(r));
      }
      return obj;
    });
  const fileStamp = () => `Supplier_Ledger_${tab.label.replace(/[^A-Za-z]+/g, '_')}_${from || 'all'}_${to || toISO(new Date())}`;

  const runExport = async (kind) => {
    setExporting(true);
    try {
      const res = await getData(REPORT_ROUTE, { ...filterParams, export: 'all' }, authToken(), false);
      if (!res?.success) throw new Error(res?.message || 'Export failed');
      const rows = res.rows || [];
      if (kind === 'csv') downloadCsv(exportObjects(rows), fileStamp());
      else if (kind === 'excel') downloadExcel(exportObjects(rows), fileStamp(), tab.label);
      else
        openPrintView({
          title: `Supplier Ledger — ${tab.label}`,
          subtitle: `${rangeLabel(from, to)} · ${rows.length} rows`,
          headers: tab.columns.map((c) => c.label),
          cells: rows.map((r) => tab.columns.map((c) => ({ value: c.print ? c.print(r) : c.text(r), className: c.numeric ? 'num' : '' }))),
        });
    } catch (err) {
      setError(err.message || 'Export failed');
    } finally {
      setExporting(false);
    }
  };

  const stats = data?.stats || {};
  const total = data?.meta?.total || 0;
  const rows = data?.tab === tab.key ? data.rows || [] : [];
  const supplierOptions = data?.filterOptions?.suppliers || [];
  const ageing = stats.ageing || [];
  const ageMax = Math.max(1, ...ageing.map((b) => b.amount));

  const cards = [
    { label: 'Total Payable', value: inr(stats.outstanding), icon: MdAccountBalance, gradient: 'linear-gradient(135deg,#f59e0b,#ea580c)', sub: `${stats.suppliersWithDue || 0} suppliers · ${stats.openInvoices || 0} open invoices`, onClick: () => goToTab(0, () => setOnlyDue('yes')), active: tab.key === 'balances' && onlyDue === 'yes' },
    { label: 'Invoiced', value: inr(stats.invoiced), icon: MdReceiptLong, gradient: 'linear-gradient(135deg,#6366f1,#8b5cf6)', sub: 'In selected period' },
    { label: 'Paid', value: inr(stats.paid), icon: MdPayments, gradient: 'linear-gradient(135deg,#10b981,#059669)', sub: 'In selected period', onClick: () => goToTab(2), active: tab.key === 'statement' },
    { label: 'Over 90 Days', value: inr(stats.overdue90), icon: MdHourglassBottom, gradient: 'linear-gradient(135deg,#f43f5e,#dc2626)', sub: `${inr(stats.overdue60)} over 60 days`, onClick: () => goToTab(1, () => setBucket('90+')), active: tab.key === 'outstanding' && bucket === '90+' },
    { label: 'Advances with Suppliers', value: inr(stats.advance), icon: MdSavings, gradient: 'linear-gradient(135deg,#14b8a6,#0d9488)', sub: 'Paid more than invoiced' },
    { label: 'Avg Days to Pay', value: stats.avgDaysToPay === null || stats.avgDaysToPay === undefined ? '—' : `${stats.avgDaysToPay} days`, icon: MdTimer, gradient: 'linear-gradient(135deg,#0ea5e9,#2563eb)', sub: 'Invoice → fully paid' },
    { label: 'Missing Bank Details', value: (stats.missingBank || 0).toLocaleString('en-IN'), icon: MdCreditCardOff, gradient: 'linear-gradient(135deg,#64748b,#334155)', sub: 'Suppliers you owe money to' },
  ];

  const activeFilterCount = [from || to, search, supplier, onlyDue, bucket, sortBy !== tab.defaultSort].filter(Boolean).length;

  return (
    <Box sx={{ p: { xs: 1, md: 3 }, fontFamily: FONT }}>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center', gap: 2, mb: 3 }}>
        <Box>
          <Typography sx={{ fontSize: 23.5, fontWeight: 800, fontFamily: FONT, color: '#0f172a' }}>Supplier Ledger Report</Typography>
          <Typography sx={{ fontSize: 12.5, color: '#64748b', fontFamily: FONT }}>What you owe each supplier, how old it is, and the full invoice / payment history</Typography>
        </Box>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          <Button variant="outlined" startIcon={<MdInfoOutline />} onClick={() => setNotesOpen(true)} sx={{ textTransform: 'none', fontWeight: 700, borderRadius: '10px', fontFamily: FONT }}>Note</Button>
          <Button startIcon={<MdFileDownload />} disabled={exporting || !total} onClick={() => runExport('csv')} sx={exportButtonSx('#16a34a', '#15803d')}>CSV</Button>
          <Button startIcon={<MdGridOn />} disabled={exporting || !total} onClick={() => runExport('excel')} sx={exportButtonSx('#0d9488', '#0f766e')}>Excel</Button>
          <Button startIcon={<MdPrint />} disabled={exporting || !total} onClick={() => runExport('print')} sx={exportButtonSx('#7c3aed', '#6d28d9')}>Print / PDF</Button>
        </Box>
      </Box>

      <Box sx={statGridSx}>
        {cards.map((c) => (
          <StatCard key={c.label} {...c} />
        ))}
      </Box>

      <Paper elevation={0} sx={{ p: 2.5, borderRadius: '16px', mb: 3, border: '1px solid #e2e8f0' }}>
        <Typography sx={{ fontWeight: 800, fontFamily: FONT, color: '#0f172a', mb: 1.5 }}>Payables ageing (days since invoice)</Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 1.5 }}>
          {ageing.map((b) => {
            const [bg, fg] = ageColors(b.key);
            const selected = tab.key === 'outstanding' && bucket === b.key;
            return (
              <Box
                key={b.key}
                onClick={() => goToTab(1, () => setBucket(selected ? '' : b.key))}
                sx={{ p: 1.5, borderRadius: '12px', background: bg, cursor: 'pointer', outline: selected ? `2px solid ${fg}` : 'none', transition: 'transform .15s', '&:hover': { transform: 'translateY(-2px)' } }}
              >
                <Typography sx={{ fontSize: 11, fontWeight: 700, color: fg, fontFamily: FONT }}>{b.label}</Typography>
                <Typography sx={{ fontSize: 18, fontWeight: 800, color: fg, fontFamily: FONT }}>{inr(b.amount)}</Typography>
                <Typography sx={{ fontSize: 11.5, color: fg, fontFamily: FONT, opacity: 0.85 }}>{b.invoices} invoices</Typography>
                <LinearProgress variant="determinate" value={(b.amount / ageMax) * 100} sx={{ mt: 0.75, height: 5, borderRadius: 3, background: 'rgba(255,255,255,.6)', '& .MuiLinearProgress-bar': { background: fg } }} />
              </Box>
            );
          })}
        </Box>
      </Paper>

      <Paper elevation={0} sx={{ p: 2.5, borderRadius: '16px', mb: 3, border: '1px solid #e2e8f0' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography sx={{ fontWeight: 800, fontFamily: FONT, color: '#0f172a' }}>
            Filters {activeFilterCount > 0 && <Chip size="small" label={`${activeFilterCount} active`} color="primary" sx={{ ml: 1, fontWeight: 700 }} />}
          </Typography>
          <Button size="small" startIcon={<MdRefresh />} onClick={resetFilters} sx={{ textTransform: 'none', fontWeight: 700, color: '#dc2626' }}>Reset</Button>
        </Box>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
          {PRESETS.map((p) => (
            <Chip
              key={p.key}
              label={p.label}
              onClick={() => applyPreset(p.key)}
              sx={{
                fontWeight: 700,
                fontFamily: FONT,
                background: preset === p.key ? 'rgb(255 209 2)' : '#f1f5f9',
                color: '#0f172a',
                border: preset === p.key ? '1px solid #0f172a' : '1px solid transparent',
                '&:hover': { background: preset === p.key ? 'rgb(255 209 2)' : '#e2e8f0' },
              }}
            />
          ))}
        </Box>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={3} lg={2}>
            <TextField label="From" type="date" size="small" fullWidth InputLabelProps={{ shrink: true }} value={from} onChange={onDateChange(setFrom)} inputProps={{ max: to || undefined }} />
          </Grid>
          <Grid item xs={12} sm={6} md={3} lg={2}>
            <TextField label="To" type="date" size="small" fullWidth InputLabelProps={{ shrink: true }} value={to} onChange={onDateChange(setTo)} inputProps={{ min: from || undefined }} />
          </Grid>
          <Grid item xs={12} sm={12} md={6} lg={3}>
            <TextField
              label="Search supplier / code / GSTIN / phone"
              size="small"
              fullWidth
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              InputProps={{ startAdornment: (<InputAdornment position="start"><MdSearch size={18} /></InputAdornment>) }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3} lg={2.5}>
            <TextField select label="Supplier" size="small" fullWidth value={supplier} onChange={setFilter(setSupplier)} SelectProps={{ MenuProps: { PaperProps: { sx: { maxHeight: 320 } } } }}>
              <MenuItem value="">All suppliers</MenuItem>
              {supplierOptions.map((s) => (
                <MenuItem key={s.value} value={s.value}>{s.label}</MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6} md={3} lg={2.5}>
            <TextField select label="Show suppliers" size="small" fullWidth value={onlyDue} onChange={setFilter(setOnlyDue)}>
              <MenuItem value="">All</MenuItem>
              <MenuItem value="yes">Only with money owed</MenuItem>
            </TextField>
          </Grid>
          {tab.key === 'outstanding' && (
            <Grid item xs={12} sm={6} md={3} lg={2.5}>
              <TextField select label="Age" size="small" fullWidth value={bucket} onChange={setFilter(setBucket)}>
                <MenuItem value="">All</MenuItem>
                {AGE.map((a) => (
                  <MenuItem key={a.key} value={a.key}>{a.label}</MenuItem>
                ))}
              </TextField>
            </Grid>
          )}
          <Grid item xs={12} sm={6} md={3} lg={2.5}>
            <TextField select label="Sort by" size="small" fullWidth value={sortBy} onChange={setFilter(setSortBy)}>
              {tab.sorts.map(([v, l]) => (
                <MenuItem key={v} value={v}>{l}</MenuItem>
              ))}
            </TextField>
          </Grid>
        </Grid>
      </Paper>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      <Paper elevation={0} sx={{ borderRadius: '16px', border: '1px solid #e2e8f0', overflow: 'hidden' }}>
        <Tabs
          value={tabIndex}
          onChange={(_, v) => goToTab(v)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            px: 1,
            borderBottom: '1px solid #e2e8f0',
            '& .MuiTab-root': { textTransform: 'none', fontWeight: 700, fontFamily: FONT, minHeight: 56 },
            '& .Mui-selected': { color: '#0f172a !important' },
            '& .MuiTabs-indicator': { background: 'rgb(255 209 2)', height: 4, borderRadius: 2 },
          }}
        >
          {TABS.map((t) => (
            <Tab key={t.key} label={t.label} />
          ))}
        </Tabs>
        <Box sx={{ px: 2.5, py: 1.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
          <Typography sx={{ fontWeight: 800, fontFamily: FONT, color: '#0f172a' }}>{tab.label}</Typography>
          <Typography sx={{ fontSize: 11.5, color: '#64748b', fontFamily: FONT }}>
            {total.toLocaleString('en-IN')} rows{tab.key === 'balances' && ' · Opening + Invoiced − Paid = Closing'}
          </Typography>
        </Box>
        {(loading || exporting) && <LinearProgress />}
        <TableContainer sx={{ maxHeight: 600 }}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                {tab.columns.map((c) => (
                  <TableCell key={c.key} align={c.center ? 'center' : c.numeric ? 'right' : 'left'} sx={{ fontWeight: 800, fontFamily: FONT, background: '#0f172a', color: '#fff', whiteSpace: 'nowrap', py: 1.5 }}>
                    {c.label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {!loading && rows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={tab.columns.length} sx={{ textAlign: 'center', color: '#94a3b8', py: 6, fontFamily: FONT }}>Nothing matches the selected filters.</TableCell>
                </TableRow>
              )}
              {rows.map((r, idx) => (
                <TableRow key={idx} hover sx={{ '&:nth-of-type(even)': { background: '#f8fafc' }, boxShadow: tab.key === 'outstanding' && r.bucket === '90+' ? 'inset 4px 0 0 #dc2626' : 'none' }}>
                  {tab.columns.map((c) => (
                    <TableCell key={c.key} align={c.center ? 'center' : c.numeric ? 'right' : 'left'} sx={{ fontFamily: FONT, whiteSpace: 'nowrap', fontSize: 11.5, fontWeight: c.bold ? 700 : 500, color: '#334155' }}>
                      {c.cell ? c.cell(r) : c.text(r)}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={total}
          page={Math.min(page, Math.max(Math.ceil(total / rowsPerPage) - 1, 0))}
          onPageChange={(_, p) => setPage(p)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
          rowsPerPageOptions={[10, 25, 50, 100]}
          sx={{ borderTop: '1px solid #e2e8f0' }}
        />
      </Paper>

      <Dialog open={notesOpen} onClose={() => setNotesOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 800, fontFamily: FONT }}>Note</DialogTitle>
        <DialogContent dividers>
          <Box component="ul" sx={{ pl: 2, m: 0 }}>
            {(data?.notes || []).map((n, i) => (
              <Typography component="li" key={i} variant="body2" sx={{ mb: 1, fontFamily: FONT }}>{n}</Typography>
            ))}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNotesOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SupplierLedgerReport;
